<script setup lang="ts">
import { CalendarClock } from 'lucide-vue-next'
import { WidgetBankPageRatesSection } from '~/components/widget/bank-page'
import type { ICurrentRatesData } from './types'
import { useDateTimeFormat } from '~/composables/useDateTimeFormat'

interface RawRate {
  buy: string
  sell: string
}
interface NormalizedRate {
  buy: number
  sell: number
}

const props = defineProps<{
  bankId: number
  bankName: string
  bankUrl: string
  ratesData: ICurrentRatesData
  ratesStatus: string
  updatedAt?: string
}>()

const { t } = useI18n()

const normalize = (input?: Record<string, RawRate> | null): Record<string, NormalizedRate> => {
  if (!input) return {}
  return Object.fromEntries(
    Object.entries(input).map(([currency, rate]) => [
      currency.toLowerCase(),
      {
        buy: parseFloat(rate.buy),
        sell: parseFloat(rate.sell)
      }
    ])
  )
}

const currentRegularRates = computed(() => normalize(props.ratesData?.currentRegularRates))
const currentCashlessRates = computed(() => normalize(props.ratesData?.currentCashlessRates))

// common rates
const rates = computed(() => {
  const regular = currentRegularRates.value
  const cashless = currentCashlessRates.value

  // all currencies
  const allCurrencies = new Set([...Object.keys(regular), ...Object.keys(cashless)])

  const regularRates: Record<string, { buy: number; sell: number }> = {}
  const cashlessRates: Record<string, { buy: number; sell: number }> = {}

  allCurrencies.forEach((currency) => {
    const regularRate = regular[currency]
    const cashlessRate = cashless[currency]

    // Check if any of the 4 rates exists
    const hasAnyRate =
      (regularRate && (regularRate.buy > 0 || regularRate.sell > 0)) ||
      (cashlessRate && (cashlessRate.buy > 0 || cashlessRate.sell > 0))

    if (hasAnyRate) {
      regularRates[currency] = {
        buy: regularRate?.buy || 0,
        sell: regularRate?.sell || 0
      }

      cashlessRates[currency] = {
        buy: cashlessRate?.buy || 0,
        sell: cashlessRate?.sell || 0
      }
    }
  })

  return {
    regular: regularRates,
    cashless: cashlessRates
  }
})

const hasCashlessData = computed(() =>
  Object.values(rates.value.cashless).some((r) => r.buy > 0 || r.sell > 0)
)

const hasRegularData = computed(() =>
  Object.values(rates.value.regular).some((r) => r.buy > 0 || r.sell > 0)
)

const allOffers = computed(() => {
  const offers = []
  if (currentRegularRates.value) {
    for (const [currency, rate] of Object.entries(currentRegularRates.value)) {
      offers.push(
        {
          '@type': 'Offer',
          name: t('widget.bank_page.rates_wrapper.buy_cash', { currency: currency.toUpperCase() }),
          description: t('widget.bank_page.rates_wrapper.buy_cash_description', {
            currency: currency.toUpperCase()
          }),
          price: rate.buy,
          priceCurrency: 'KGS'
        },
        {
          '@type': 'Offer',
          name: t('widget.bank_page.rates_wrapper.sell_cash', { currency: currency.toUpperCase() }),
          description: t('widget.bank_page.rates_wrapper.sell_cash_description', {
            currency: currency.toUpperCase()
          }),
          price: rate.sell,
          priceCurrency: 'KGS'
        }
      )
    }
  }
  if (currentCashlessRates.value) {
    for (const [currency, rate] of Object.entries(currentCashlessRates.value)) {
      offers.push(
        {
          '@type': 'Offer',
          name: t('widget.bank_page.rates_wrapper.buy_cashless', {
            currency: currency.toUpperCase()
          }),
          description: t('widget.bank_page.rates_wrapper.buy_cashless_description', {
            currency: currency.toUpperCase()
          }),
          price: rate.buy,
          priceCurrency: 'KGS'
        },
        {
          '@type': 'Offer',
          name: t('widget.bank_page.rates_wrapper.sell_cashless', {
            currency: currency.toUpperCase()
          }),
          description: t('widget.bank_page.rates_wrapper.sell_cashless_description', {
            currency: currency.toUpperCase()
          }),
          price: rate.sell,
          priceCurrency: 'KGS'
        }
      )
    }
  }
  // Missing rates parse to NaN, which JSON-LD serializes as null and crashes Safari's extractor
  return offers.filter((offer) => offer.price > 0)
})

const currencyServiceId = computed(() => props.bankUrl + '#/schema/currency-conversion-service')

useSchemaOrg([
  {
    '@id': currencyServiceId,
    '@type': 'CurrencyConversionService',
    name: t('widget.bank_page.rates_wrapper.current_rates', { bankName: props.bankName }),
    description: t('widget.bank_page.rates_wrapper.rates_description'),
    provider: {
      '@type': 'Organization',
      name: props.bankName,
      url: props.bankUrl
    },
    // Use allOffers from computed
    offers: allOffers
  }
])
</script>

<template>
  <div class="flex flex-col gap-6">
    <p v-if="props.updatedAt" class="flex items-center gap-1 text-xs text-gray-500">
      <CalendarClock class="size-3.5 shrink-0" />
      {{ $t('widget.bank_page.rates_wrapper.rates_updated_at') }}
      {{ useDateTimeFormat(props.updatedAt) }}
    </p>
    <div
      v-if="props.ratesStatus === 'pending' || hasRegularData"
      class="grid grid-cols-1 gap-4 md:items-start xl:grid-cols-[1fr_2fr] xl:gap-6"
    >
      <div>
        <h2 class="text-lg font-semibold text-gray-900">
          {{ $t('widget.bank_page.rates_wrapper.cash_rates_heading') }}
        </h2>
        <p class="mt-1 text-sm text-gray-600">
          {{ $t('widget.bank_page.rates_wrapper.cash_rates_description') }}
        </p>
      </div>
      <div>
        <WidgetBankPageRatesSection
          :rates="rates.regular"
          :is-loading="props.ratesStatus === 'pending'"
        />
      </div>
    </div>
    <template v-if="props.ratesStatus === 'pending' || hasCashlessData">
      <div
        v-if="props.ratesStatus === 'pending' || hasRegularData"
        class="my-2 border-t border-gray-200"
      />
      <div class="grid grid-cols-1 gap-4 md:items-start xl:grid-cols-[1fr_2fr] xl:gap-6">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">
            {{ $t('widget.bank_page.rates_wrapper.cashless_rates_heading') }}
          </h2>
          <p class="mt-1 text-sm text-gray-600">
            {{ $t('widget.bank_page.rates_wrapper.cashless_rates_description') }}
          </p>
        </div>
        <div>
          <WidgetBankPageRatesSection
            :rates="rates.cashless"
            :is-loading="props.ratesStatus === 'pending'"
          />
        </div>
      </div>
    </template>
  </div>
</template>
